import { useCallback, useEffect, useState } from 'react'

import { DndContext, MouseSensor, closestCenter, useSensor, useSensors } from '@dnd-kit/core'
import { restrictToVerticalAxis } from '@dnd-kit/modifiers'
import { arrayMove, SortableContext, verticalListSortingStrategy, useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { flexRender } from '@tanstack/react-table'
import { TableBody, TableRow, TableCell, cn } from 'twyne-new.ui'

import { useGrid } from '@Components/Grid/context'

import { useTableContext } from '../context';
import { pinnedCellProps, usePinnedColumns } from '../pinned'

import DragContext from './DragContext';

export default function Body() {
  const table = useTableContext()
  const sensors = useSensors(useSensor(MouseSensor, {}))
  const { events: { handleUpdateEntries } } = useGrid()

  const [data, setData] = useState([])
  const [ids, setIds] = useState([])

  const { rows } = table.getRowModel()

  useEffect(() => {
    setData(rows)
    setIds(rows.map(({ original: { id } }) => id))
  }, [rows])

  const handleDragEnd = useCallback(({ active, over }) => {
    if (active && over && active.id !== over.id) {
      setData(data => {
        const newData = arrayMove(data, ids.indexOf(active.id), ids.indexOf(over.id))
        setIds(newData.map(({ original: { id } }) => id))
        handleUpdateEntries && handleUpdateEntries(newData)
        return newData
      })
    }
  }, [ids, handleUpdateEntries])

  return <DndContext
    collisionDetection={closestCenter}
    modifiers={[restrictToVerticalAxis]}
    onDragEnd={handleDragEnd}
    sensors={sensors}
  >
    <TableBody>
      <SortableContext items={ids} strategy={verticalListSortingStrategy}>
        {rows.length === 0 ? <Empty /> : <>
          {data.map(({ id, original, getVisibleCells }, idx) => <>
            <Row key={id} id={original.id} getVisibleCells={getVisibleCells} idx={idx} />
          </>)}
        </>}
      </SortableContext>
    </TableBody>
  </DndContext>
}

function Row({ id, getVisibleCells, idx }) {
  const { transform, transition, setNodeRef, isDragging, attributes, listeners } = useSortable({ id })
  const { pinnedIndexes, offsets } = usePinnedColumns()

  const style = {
    transform: CSS.Transform.toString(transform),
    transition: transition,
    opacity: isDragging ? 0.8 : 1,
    zIndex: isDragging ? 1 : 0,
    position: 'relative',
  }

  return <DragContext.Provider value={{ attributes, listeners, isDragging }}>
    <TableRow ref={setNodeRef} style={style} data-index={id} className={idx % 2 && 'twy-bg-base'}>
      {/* <TableCell><Checkbox /></TableCell> */}
      {getVisibleCells().map(({ id, column, getContext }, cellIndex) => {
        const pinned = pinnedCellProps({ index: cellIndex, rowIndex: idx, pinnedIndexes, offsets })

        return <Cell
          key={id}
          className={cn(column.columnDef.cellClassName, pinned.className)}
          style={pinned.style}
          cell={column.columnDef.cell}
          context={getContext()}
        />
      })}
    </TableRow>
  </DragContext.Provider>
}

function Cell({ cell, className, style, context }) {
  const content = flexRender(cell, context)
  return <TableCell className={className} style={style}>
    {content}
  </TableCell>
}

function Empty() {
  const { store: { loading } } = useGrid()

  if (!loading) {
    return <TableRow>
      <TableCell colSpan={100} className="text-center">
        No content to show.
      </TableCell>
    </TableRow>
  }
}
